import storage from 'app/services/storage';
import { makeTree } from 'app/shared/components/common/Tree';
import _ from 'lodash';

import { DOCUMENT_EXTRACTION_FIELD_UPDATE_TYPE } from 'constants/documentExtraction';
import { GROUP_DIVIDER, RESULT_FIELD_ID_PARTS_DIVIDER } from 'constants/documentExtractionFields';
import { DOC_FIELD_TYPES, dropDownDocumentFieldTypes } from 'constants/documentFieldTypes';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { getConditionalObject } from 'utils/data';
import { checkIsContainerType } from 'utils/documentFieldTypes';
import { generateDocValidationSchema } from 'utils/validation';

import { LOCAL_STORAGE_FORM_KEY } from './EditExtractionFieldsForm.constants';

export const setDefaultValuesForBooleanFields = (acc, { id, type }) => {
  if (type === DOC_FIELD_TYPES.BOOL) {
    acc[id] = false;
  }

  return acc;
};
export const setDefaultValuesForMultipleDropdownFields = (acc, { id, type }) => {
  if (type === DOC_FIELD_TYPES.MULTIPLE_SELECTION_DROP_DOWN) {
    acc[id] = [];
  }

  return acc;
};

export const cloneNestedFields = ({ fields, fieldIdPostfix }) => {
  const allClonedFields = [];

  const handleField = field => {
    let clonedField = {
      ...field,
      id: `${
        field.id.split(RESULT_FIELD_ID_PARTS_DIVIDER)[0]
      }${RESULT_FIELD_ID_PARTS_DIVIDER}${fieldIdPostfix}`,
    };

    if (field.nestedFields?.length) {
      const nestedFields = field.nestedFields.map(handleField);

      clonedField = {
        ...clonedField,
        nestedFields,
      };
    }

    const nestedGroups = Object.values(field.nestedGroups || {});

    if (nestedGroups.length) {
      clonedField = {
        ...clonedField,
        nestedGroups: {},
      };
    }

    allClonedFields.push(clonedField);

    return clonedField;
  };

  const clonedStructuredFields = _.cloneDeep(fields).map(handleField);

  return { clonedStructuredFields, allClonedFields };
};

const createDropdownInitialValues = (acc, field) => {
  if (field.nestedFields?.length) {
    if (field.type === DOC_FIELD_TYPES.MULTIPLE_SELECTION_DROP_DOWN) {
      return { ...acc, [field.id]: [] };
    }

    return field.nestedFields.reduce(createDropdownInitialValues, acc);
  }

  return acc;
};

const filterMappedAiFieldsByConfidence = (mappedAiFields, aiExtractionFieldConfidenceThreshold) =>
  mappedAiFields?.pages.reduce((acc, { fields }) => {
    const filteredFields = fields.filter(
      ({ confidence }) =>
        confidence >= parseInt(aiExtractionFieldConfidenceThreshold.propertyValue),
    );

    return [...acc, ...filteredFields];
  }, []);

const getPrefilledExtractionFields = (
  fieldsGroupedByAiExtractionFieldName,
  aiFieldsWithConfidenceAboveThreshold,
) =>
  aiFieldsWithConfidenceAboveThreshold?.reduce(
    (acc, { schema, value }) => ({
      ...acc,
      [fieldsGroupedByAiExtractionFieldName[schema?.name].id]: value,
    }),
    {},
  );

const findContainerInTree = ({ treeStructure, containerId }) => {
  let foundContainer = null;

  const handleField = ({ field }) => {
    if (field.id.includes(containerId)) {
      foundContainer = field;

      return true;
    }

    if (!field.nestedFields?.length) {
      return false;
    }

    return field.nestedFields.find(nestedField => handleField({ field: nestedField }));
  };

  treeStructure.find(field => handleField({ field }));

  return foundContainer;
};

const insertClonedGroupIntoStructure = ({
  treeStructure,
  dynamicContainer,
  clonedGroup,
  groupId,
}) => {
  let isContainerFound = false;

  const handleField = field => {
    if (isContainerFound) {
      return;
    }

    const isSavedDynamicContainer = field.id === dynamicContainer.id;

    if (isSavedDynamicContainer) {
      isContainerFound = true;

      dynamicContainer.nestedGroups = {
        ...dynamicContainer.nestedGroups,
        [groupId]: clonedGroup,
      };
    }

    if (!field.nestedFields?.length) {
      return;
    }

    field.nestedFields.forEach(handleField);
  };

  treeStructure.forEach(handleField);
};

export const prepareExtractionFields = (
  documentExtractionFields,
  setDuplicatedFields,
  setSchema,
  setDocumentExtractionFieldsTree,
  setForm,
  mappedAiFieldsResult,
  fieldsGroupedByAiExtractionFieldName,
  aiExtractionFieldConfidenceThreshold,
  initialForm,
  isGenAi,
  skipBooleanDefaults = false,
) => {
  // A worksheet asks Yes/No questions: an unanswered one must stay empty rather than read as "No".
  let initialFormValues = skipBooleanDefaults
    ? {}
    : documentExtractionFields.reduce(setDefaultValuesForBooleanFields, {});

  if (documentExtractionFields.length) {
    const storedForm = initialForm || storage.getParsedItem(LOCAL_STORAGE_FORM_KEY);
    const isFormSaved = !_.isEmpty(storedForm);
    const duplicatedFields = [];
    let savedGeneratedFields = [];
    let schema = generateDocValidationSchema(documentExtractionFields);
    let fieldsTree = makeTree(documentExtractionFields);

    const dropdownFieldInitialValues = fieldsTree.reduce(createDropdownInitialValues, {});

    const aiFieldsWithConfidenceAboveThreshold = filterMappedAiFieldsByConfidence(
      mappedAiFieldsResult,
      aiExtractionFieldConfidenceThreshold,
    );

    const prefilledFieldsByAiModel = getPrefilledExtractionFields(
      fieldsGroupedByAiExtractionFieldName,
      aiFieldsWithConfidenceAboveThreshold,
    );

    if (!_.isEmpty(dropdownFieldInitialValues)) {
      initialFormValues = { ...initialFormValues, ...dropdownFieldInitialValues };
    }

    if (isFormSaved) {
      const formWithSavedFields = _.omitBy(storedForm, (__, key) => {
        const [fieldId] = key.split(RESULT_FIELD_ID_PARTS_DIVIDER);

        return !documentExtractionFields.find(({ id }) => id === fieldId);
      });

      savedGeneratedFields = Object.keys(storedForm).filter(formKey =>
        formKey.includes(GROUP_DIVIDER),
      );
      initialFormValues = {
        ...initialFormValues,
        ...formWithSavedFields,
        ...(isGenAi ? prefilledFieldsByAiModel : {}),
      };

      if (savedGeneratedFields.length) {
        const newDocumentExtractionFieldsTree = savedGeneratedFields.reduce(
          (acc, formKey) => {
            const pathParts = formKey.split(RESULT_FIELD_ID_PARTS_DIVIDER).reverse();

            const handlePathPart = ({ structure, remainingPathParts }) => {
              const pathPart = remainingPathParts.shift();

              if (!pathPart) {
                return;
              }

              const [dynamicContainerId, groupId] = pathPart.split(GROUP_DIVIDER);

              if (!groupId) {
                handlePathPart({ structure, remainingPathParts });
                return;
              }

              const dynamicContainer = findContainerInTree({
                treeStructure: structure,
                containerId: dynamicContainerId,
              });
              const isContainerHasNoGroup =
                !!dynamicContainer && !dynamicContainer.nestedGroups?.[groupId];

              if (isContainerHasNoGroup) {
                const fieldIdPostfix = _.difference(pathParts, remainingPathParts)
                  .reverse()
                  .join(RESULT_FIELD_ID_PARTS_DIVIDER);

                const { clonedStructuredFields, allClonedFields } = cloneNestedFields({
                  fields: dynamicContainer.nestedFields,
                  fieldIdPostfix: fieldIdPostfix,
                });

                insertClonedGroupIntoStructure({
                  treeStructure: structure,
                  dynamicContainer,
                  clonedGroup: clonedStructuredFields,
                  groupId,
                });

                duplicatedFields.push(...allClonedFields);
              }

              const nextStructure = dynamicContainer?.nestedGroups[groupId];

              handlePathPart({ structure: nextStructure, remainingPathParts });
            };

            handlePathPart({ structure: acc, remainingPathParts: [...pathParts] });

            return acc;
          },
          [...fieldsTree],
        );

        schema = generateDocValidationSchema([...documentExtractionFields, ...duplicatedFields]);
        fieldsTree = newDocumentExtractionFieldsTree;
      }
    }

    if (isGenAi && aiFieldsWithConfidenceAboveThreshold?.length) {
      initialFormValues = prefilledFieldsByAiModel;
    }

    setDuplicatedFields(duplicatedFields);
    setSchema(schema);
    setDocumentExtractionFieldsTree(fieldsTree);
  }

  setForm(initialFormValues);
};

export const getIsErrorStatusForbiddenOrNotFound = error =>
  [HTTP_RESPONSE_STATUS_CODES.FORBIDDEN, HTTP_RESPONSE_STATUS_CODES.NOT_FOUND].includes(
    error?.response?.status,
  );

export const checkNestedFieldsIsNotEmpty = ({ type, nestedFields }) => {
  const isContainerType = checkIsContainerType(type);
  const isDropdownType = dropDownDocumentFieldTypes.includes(type);

  if (isDropdownType) {
    return nestedFields?.length > 0;
  }

  if (isContainerType) {
    return !!nestedFields?.some(checkNestedFieldsIsNotEmpty);
  }

  return true;
};

const getExtractionFieldValue = value => {
  if (Array.isArray(value)) {
    return value.map(({ name }) => name).join(';');
  }

  if (_.isObject(value)) {
    return value?.name || '';
  }

  return value;
};

const getExtractionFieldValueType = ({ isTableObject, isInTable }) => {
  if (isTableObject) {
    return DOCUMENT_EXTRACTION_FIELD_UPDATE_TYPE.TABLE_OBJECT;
  }

  if (isInTable) {
    return DOCUMENT_EXTRACTION_FIELD_UPDATE_TYPE.TABLE;
  }

  return DOCUMENT_EXTRACTION_FIELD_UPDATE_TYPE.ORDINARY;
};

const getExtractionFieldsValuesTree = ({ form, documentExtractionFieldsTree, isInTable }) => {
  const fieldsMap = documentExtractionFieldsTree.reduce((acc, field) => {
    const { id } = field;
    const [extractionFieldId] = id.split(RESULT_FIELD_ID_PARTS_DIVIDER);
    const value = form[id];
    // A dropdown's `nestedFields` are its selectable OPTIONS, not sub-fields: the picked
    // value lives on the dropdown's own form key (form[id] = { id, name } | [{ id, name }]),
    // never on the option nodes. Treating it as a container recurses into optionless children,
    // finds nothing, and drops the field on save — so a dropdown-only edit serializes to zero
    // results and trips "At least one field should be extracted". Serialize it as a leaf via
    // getExtractionFieldValue (its object/array handling maps to the option name string(s)).
    const isDropdownType = dropDownDocumentFieldTypes.includes(field.type);
    const isNestedFieldsExist = !!field.nestedFields?.length && !isDropdownType;
    const isTableType = field.type === DOC_FIELD_TYPES.TABLE;

    const fieldValue = getExtractionFieldValue(value);
    const isEmpty = !acc[extractionFieldId];

    let children = [];

    if (
      ((!value && !_.isBoolean(value)) || (Array.isArray(value) && !value.length)) &&
      !isNestedFieldsExist
    ) {
      return acc;
    }

    if (isNestedFieldsExist) {
      const nestedGroupFields = Object.values(field.nestedGroups || {}).flat();
      const allNestedFields = [...field.nestedFields, ...nestedGroupFields];

      children = getExtractionFieldsValuesTree({
        form,
        documentExtractionFieldsTree: allNestedFields,
        isInTable: true,
      });

      if (!children.length) {
        return acc;
      }

      if (!isTableType) {
        if (isEmpty) {
          acc[extractionFieldId] = children;
        } else {
          const rowNumber = acc[extractionFieldId].length + 1;

          const updatedChildren = children.map(child => ({
            ...child,
            rowNumber,
          }));

          acc[extractionFieldId] = [...acc[extractionFieldId], ...updatedChildren];
        }

        return acc;
      }
    }

    if (isEmpty) {
      acc[extractionFieldId] = [
        {
          extractionFieldId,
          value: isTableType ? '' : fieldValue,
          type: getExtractionFieldValueType({ isTableObject: isTableType, isInTable }),
          ...getConditionalObject(isInTable, {
            rowNumber: 1,
          }),
          ...getConditionalObject(children.length, {
            children,
          }),
        },
      ];

      return acc;
    }

    acc[extractionFieldId].push({
      extractionFieldId,
      value: isTableType ? '' : fieldValue,
      type: getExtractionFieldValueType({ isTableObject: isTableType, isInTable }),
      ...getConditionalObject(isInTable, {
        rowNumber: acc[extractionFieldId].length + 1,
      }),
      ...getConditionalObject(children.length, {
        children,
      }),
    });

    return acc;
  }, {});

  return Object.values(fieldsMap).flat();
};

export const getExtractionFieldsToSend = ({ form, documentExtractionFieldsTree }) => {
  const fieldsValuesTree = getExtractionFieldsValuesTree({
    form,
    documentExtractionFieldsTree,
  });

  return { extractionFields: fieldsValuesTree };
};

export const removeDynamicGroupFromTree = ({ groupId, fieldsTree }) => {
  let isGroupFound = false;

  const handleItem = item => {
    if (isGroupFound) {
      return item;
    }

    const isGroupInDynamicContainer = !!item.nestedGroups?.[groupId];

    if (isGroupInDynamicContainer) {
      isGroupFound = true;

      return {
        ...item,
        nestedGroups: _.omit(item.nestedGroups, groupId),
      };
    }

    const updatedNestedFields = item.nestedFields?.map(handleItem) ?? [];

    let updatedNestedGroups = {};

    const nestedGroups = Object.entries(item.nestedGroups || {});

    if (nestedGroups.length) {
      updatedNestedGroups = nestedGroups.reduce((acc, [groupId, fields]) => {
        acc[groupId] = fields.map(handleItem);

        return acc;
      }, {});
    }

    return {
      ...item,
      nestedFields: updatedNestedFields,
      nestedGroups: updatedNestedGroups,
    };
  };

  return fieldsTree.map(handleItem);
};
